/**
 * dataValidation.ts
 * Utility for profile-completeness scoring and data-quality checks across SILAG SIMS.
 */

// ─── Profile Completeness ─────────────────────────────────────────────────────

export interface ProfileField {
  key: string;
  label: string;
  weight: number; // 1–3: 1 = nice-to-have, 2 = important, 3 = critical
}

export interface CompletenessResult {
  score: number;          // 0–100
  level: 'complete' | 'good' | 'partial' | 'incomplete';
  missingFields: string[];
  filledFields: string[];
  totalWeight: number;
  earnedWeight: number;
}

const USER_PROFILE_FIELDS: ProfileField[] = [
  { key: 'full_name',   label: 'Full Name',   weight: 3 },
  { key: 'email',       label: 'Email',       weight: 3 },
  { key: 'role',        label: 'Role',        weight: 3 },
  { key: 'phone',       label: 'Phone',       weight: 2 },
  { key: 'department',  label: 'Department',  weight: 1 },
];

const STUDENT_PROFILE_FIELDS: ProfileField[] = [
  { key: 'full_name',         label: 'Full Name',         weight: 3 },
  { key: 'student_number',    label: 'Student Number',    weight: 3 },
  { key: 'enrollment_status', label: 'Enrollment Status', weight: 3 },
  { key: 'programs',          label: 'Program',           weight: 3 },
  { key: 'campuses',          label: 'Campus',            weight: 2 },
  { key: 'year_of_study',     label: 'Year of Study',     weight: 2 },
  { key: 'semester',          label: 'Semester',          weight: 2 },
  { key: 'fee_status',        label: 'Fee Status',        weight: 2 },
  { key: 'gpa',               label: 'GPA',               weight: 1 },
];

function isValuePresent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return Boolean(value);
}

export function computeCompleteness(
  record: Record<string, unknown>,
  fields: ProfileField[]
): CompletenessResult {
  let totalWeight = 0;
  let earnedWeight = 0;
  const missingFields: string[] = [];
  const filledFields: string[] = [];

  for (const field of fields) {
    totalWeight += field.weight;
    if (isValuePresent(record[field.key])) {
      earnedWeight += field.weight;
      filledFields.push(field.label);
    } else {
      missingFields.push(field.label);
    }
  }

  const score = totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0;

  const level: CompletenessResult['level'] =
    score === 100 ? 'complete' :
    score >= 80   ? 'good' :
    score >= 50   ? 'partial': 'incomplete';

  return { score, level, missingFields, filledFields, totalWeight, earnedWeight };
}

export function computeUserCompleteness(user: Record<string, unknown>): CompletenessResult {
  return computeCompleteness(user, USER_PROFILE_FIELDS);
}

export function computeStudentCompleteness(student: Record<string, unknown>): CompletenessResult {
  return computeCompleteness(student, STUDENT_PROFILE_FIELDS);
}

// ─── Completeness Badge Metadata ─────────────────────────────────────────────

export interface BadgeMeta {
  label: string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  dotClass: string;
}

export function getCompletenessBadge(level: CompletenessResult['level']): BadgeMeta {
  switch (level) {
    case 'complete':
      return {
        label: 'Complete',
        colorClass: 'text-emerald-700',
        bgClass: 'bg-emerald-50',
        borderClass: 'border-emerald-200',
        dotClass: 'bg-emerald-500',
      };
    case 'good':
      return {
        label: 'Good',
        colorClass: 'text-blue-700',
        bgClass: 'bg-blue-50',
        borderClass: 'border-blue-200',
        dotClass: 'bg-blue-500',
      };
    case 'partial':
      return {
        label: 'Partial',
        colorClass: 'text-amber-700',
        bgClass: 'bg-amber-50',
        borderClass: 'border-amber-200',
        dotClass: 'bg-amber-400',
      };
    case 'incomplete':
      return {
        label: 'Incomplete',
        colorClass: 'text-red-700',
        bgClass: 'bg-red-50',
        borderClass: 'border-red-200',
        dotClass: 'bg-red-400',
      };
  }
}

// ─── Admin Config Validation ──────────────────────────────────────────────────

export interface SettingValidationResult {
  key: string;
  label: string;
  isBlank: boolean;
  severity: 'critical' | 'warning' | 'info';
}

/** Critical institution settings that must not be blank */
const CRITICAL_SETTING_KEYS: Record<string, { label: string; severity: 'critical' | 'warning' | 'info' }> = {
  institution_name:    { label: 'Institution Name',    severity: 'critical' },
  institution_email:   { label: 'Institution Email',   severity: 'critical' },
  institution_phone:   { label: 'Institution Phone',   severity: 'warning' },
  institution_address: { label: 'Institution Address', severity: 'warning' },
  academic_year:       { label: 'Academic Year',       severity: 'critical' },
  current_semester:    { label: 'Current Semester',    severity: 'critical' },
  fee_currency:        { label: 'Fee Currency',        severity: 'warning' },
  support_email:       { label: 'Support Email',       severity: 'warning' },
};

export function validateInstitutionSettings(
  settings: Array<{ key: string; value: string; label: string }>
): SettingValidationResult[] {
  const results: SettingValidationResult[] = [];

  for (const setting of settings) {
    const meta = CRITICAL_SETTING_KEYS[setting.key];
    if (!meta) continue;

    const isBlank = !setting.value || setting.value.trim().length === 0;
    results.push({
      key: setting.key,
      label: setting.label || meta.label,
      isBlank,
      severity: meta.severity,
    });
  }

  return results;
}

export function getBlankSettingsWarnings(
  settings: Array<{ key: string; value: string; label: string }>
): SettingValidationResult[] {
  return validateInstitutionSettings(settings).filter(r => r.isBlank);
}

// ─── Data Quality Metrics ─────────────────────────────────────────────────────

export interface DataQualityMetrics {
  tableName: string;
  displayName: string;
  totalRows: number;
  completeRows: number;
  incompleteRows: number;
  completenessPercent: number;
  qualityLevel: 'excellent' | 'good' | 'fair' | 'poor';
  issues: string[];
}

export function computeQualityLevel(
  pct: number
): DataQualityMetrics['qualityLevel'] {
  if (pct >= 95) return 'excellent';
  if (pct >= 80) return 'good';
  if (pct >= 60) return 'fair';
  return 'poor';
}

export function getQualityLevelMeta(level: DataQualityMetrics['qualityLevel']): {
  label: string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
} {
  switch (level) {
    case 'excellent':
      return { label: 'Excellent', colorClass: 'text-emerald-700', bgClass: 'bg-emerald-50', borderClass: 'border-emerald-200' };
    case 'good':
      return { label: 'Good', colorClass: 'text-blue-700', bgClass: 'bg-blue-50', borderClass: 'border-blue-200' };
    case 'fair':
      return { label: 'Fair', colorClass: 'text-amber-700', bgClass: 'bg-amber-50', borderClass: 'border-amber-200' };
    case 'poor':
      return { label: 'Poor', colorClass: 'text-red-700', bgClass: 'bg-red-50', borderClass: 'border-red-200' };
  }
}

/** Aggregate completeness across multiple records for a given set of required fields */
export function aggregateCompleteness(
  rows: Record<string, unknown>[],
  requiredFields: string[]
): { completeRows: number; incompleteRows: number; completenessPercent: number } {
  if (rows.length === 0) {
    return { completeRows: 0, incompleteRows: 0, completenessPercent: 100 };
  }

  let completeRows = 0;
  for (const row of rows) {
    const allFilled = requiredFields.every(f => isValuePresent(row[f]));
    if (allFilled) completeRows++;
  }

  const incompleteRows = rows.length - completeRows;
  const completenessPercent = Math.round((completeRows / rows.length) * 100);

  return { completeRows, incompleteRows, completenessPercent };
}
